import type { FeatureModule } from '@platform/features';
import { SecurityRegistry } from '@core/be/security/SecurityRegistry';
import { SubscriptionFeatureRegistry } from '@core/be/subscription/SubscriptionFeatureRegistry';
import { AudioTranscriptionFeatureModule } from '@audio-transcription/AudioTranscriptionFeatureModule';
import { CommentsFeatureModule } from '@comments/CommentsFeatureModule';
import { MindmapFeatureModule } from '@mindmap/MindmapFeatureModule';
import { OrganizationFeatureModule } from '@/features/organizations/OrganizationFeatureModule';
import { SearchFeatureModule } from '@search/SearchFeatureModule';
import { SwaggerFeatureModule } from '@swagger/SwaggerFeatureModule';
import { VaultFeatureModule } from '@vaults/VaultFeatureModule';
import { FeedbackFeatureModule } from '@feedback/FeedbackFeatureModule';
import { ExcalidrawFeatureModule } from '@excalidraw/ExcalidrawFeatureModule';

export class FeatureRegistryService {
  private static instance: FeatureRegistryService | null = null;

  private readonly modules: FeatureModule[] = [
    AudioTranscriptionFeatureModule.getDefinition(),
    CommentsFeatureModule.getDefinition(),
    OrganizationFeatureModule.getDefinition(),
    SearchFeatureModule.getDefinition(),
    SwaggerFeatureModule.getDefinition(),
    MindmapFeatureModule.getDefinition(),
    ExcalidrawFeatureModule.getDefinition(),
    VaultFeatureModule.getDefinition(),
    FeedbackFeatureModule.getDefinition(),
  ];
  private readonly securityRegistry = SecurityRegistry.getInstance();
  private readonly subscriptionFeatureRegistry = SubscriptionFeatureRegistry.getInstance();
  private bootstrapped = false;

  private constructor() {}

  public static getInstance(): FeatureRegistryService {
    if (!FeatureRegistryService.instance) {
      FeatureRegistryService.instance = new FeatureRegistryService();
    }
    return FeatureRegistryService.instance;
  }

  public getFeatureModules(): FeatureModule[] {
    return this.modules;
  }

  public isFeatureEnabled(name: string): boolean {
    const featureModule = this.modules.find((entry) => entry.name === name);
    if (!featureModule) return true;
    if (typeof featureModule.isEnabled === 'function') {
      try {
        return Boolean(featureModule.isEnabled());
      } catch (error) {
        console.warn(`⚠️  Feature "${featureModule.name}" isEnabled() threw:`, error);
        return false;
      }
    }
    if (typeof featureModule.enabled === 'boolean') {
      return featureModule.enabled;
    }
    return true;
  }

  public ensureBootstrapped(): void {
    if (this.bootstrapped) return;

    // 1. Register subscription features FIRST (before privileges)
    this.registerSubscriptionFeatures();

    // 2. Register privileges from feature modules (MUST succeed - fail-fast)
    for (const feature of this.modules) {
      // No try-catch: privilege registration failures are fatal
      // If this throws, the app should not start with incomplete security config
      this.securityRegistry.registerFeature(feature);
    }

    // 3. Validate subscription features map to registered privileges
    const validationErrors = this.subscriptionFeatureRegistry.validatePrivileges(
      (id) => this.securityRegistry.getPrivilege(id)
    );
    if (validationErrors.length > 0) {
      throw new Error(
        `Subscription feature validation failed - privileges not registered:\n${validationErrors.map((e) => `  - ${e}`).join('\n')}`
      );
    }

    this.securityRegistry.lock();
    this.subscriptionFeatureRegistry.lock();

    // 4. Fire-and-forget async bootstraps
    for (const feature of this.modules) {
      const result = feature.bootstrap?.();
      // If bootstrap returns a Promise, handle it
      if (result && typeof result === 'object' && 'then' in result) {
        (result as Promise<void>).catch((error: any) => {
          console.warn(`⚠️  Failed to bootstrap feature "${feature.name}":`, error);
        });
      }
    }

    this.bootstrapped = true;
  }

  /**
   * Register subscription-gated features in the SubscriptionFeatureRegistry.
   * These define which features (bundles of privileges) can be toggled per tier.
   */
  private registerSubscriptionFeatures(): void {
    // Search features
    this.subscriptionFeatureRegistry.registerFeature({
      id: 'search:basic',
      name: 'Basic Search',
      description: 'BM25 full-text search',
      category: 'search',
      privileges: ['search:basic'],
      defaultEnabled: true,
    });

    this.subscriptionFeatureRegistry.registerFeature({
      id: 'search:semantic',
      name: 'Semantic Search',
      description: 'AI-powered semantic search',
      category: 'search',
      privileges: ['search:advanced'],
      defaultEnabled: false,
    });

    // Collaboration features
    this.subscriptionFeatureRegistry.registerFeature({
      id: 'comments',
      name: 'Comments',
      description: 'Inline comments and discussions',
      category: 'collaboration',
      privileges: ['comments:create', 'comments:edit', 'comments:delete', 'comments:resolve', 'comments:read'],
      defaultEnabled: false,
    });

    this.subscriptionFeatureRegistry.registerFeature({
      id: 'collaboration:realtime',
      name: 'Real-time Collaboration',
      description: 'Live multi-user editing with cursors.',
      category: 'collaboration',
      privileges: [],
      defaultEnabled: false,
    });

    // Content features
    this.subscriptionFeatureRegistry.registerFeature({
      id: 'transcription',
      name: 'Audio Transcription',
      description: 'Convert audio to text',
      category: 'content',
      privileges: ['audio:transcribe'],  // Matches AudioTranscriptionFeatureModule.privilegeIds.transcribe
      defaultEnabled: false,
    });

    this.subscriptionFeatureRegistry.registerFeature({
      id: 'mindmap',
      name: 'Mind Maps',
      description: 'Create visual mind maps',
      category: 'content',
      privileges: ['mindmap:create', 'mindmap:edit'],
      defaultEnabled: false,
    });

    this.subscriptionFeatureRegistry.registerFeature({
      id: 'excalidraw',
      name: 'Excalidraw Diagrams',
      description: 'Create hand-drawn diagrams',
      category: 'content',
      privileges: ['excalidraw:create', 'excalidraw:edit'],
      defaultEnabled: true, // Enabled by default like search:basic
    });
  }
}

export const featureRegistryService = FeatureRegistryService.getInstance();
